import type { Flag } from '@/state/flags/flag-types';
import { useState } from 'react';
import { config } from 'config';
import { toast } from '@/components/ui/sonner';
import { flagBrowserSnippet, flagCurlSnippet, flagSdkSnippet } from '@/state/flags/flag-snippets';
import { Snippet } from '../../Websites/List/ScriptsModal';
import { GuideLink } from '../../Experiments/Details/ExperimentSettings/ServerSideConfig/GuideLink';
import { FlagSection } from './FlagSection';
import { FLAG_COPY } from '../copy';

type FlagCodeProps = { flag: Flag };

const COPY = FLAG_COPY.code;

/** How long "copied" stays on a snippet. */
const COPIED_MS = 3000;

/**
 * A code flag's snippets, filled in with its key and website, in the Server-side experiment's
 * style: the SDK, the browser read, then the HTTP call for a stack without it. The payloads each
 * side carries have a card of their own (FlagPayloads).
 */
export const FlagCode = ({ flag }: FlagCodeProps) => {
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const params = {
    flagKey: flag.flagKey ?? 'your-flag-key',
    websiteId: flag.websiteId,
    bucketOn: flag.bucketOn ?? 'visitor',
    trackingUrl: config.TRACKING_URL,
  };
  const sdk = flagSdkSnippet(params);
  const browser = flagBrowserSnippet(params);
  const curl = flagCurlSnippet(params);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      toast.error(COPY.copyFailed);
      return;
    }
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), COPIED_MS);
  };

  return (
    <FlagSection
      title={COPY.title}
      help={COPY.help}
      description={COPY.description}
      tourAnchor="flag-code"
      actions={<GuideLink guide="sdk" label={COPY.guide} className="text-meta" />}
    >
      <div className="grid gap-3">
        <Snippet
          title={COPY.sdkTitle}
          description={COPY.sdkDescription}
          snippet={sdk}
          isCopied={copiedText === sdk}
          onCopy={() => copy(sdk)}
        />
        <Snippet
          title={COPY.browserTitle}
          description={COPY.browserDescription}
          snippet={browser}
          isCopied={copiedText === browser}
          onCopy={() => copy(browser)}
        />
        <Snippet
          title={COPY.curlTitle}
          description={COPY.curlDescription}
          snippet={curl}
          isCopied={copiedText === curl}
          onCopy={() => copy(curl)}
        />
      </div>
    </FlagSection>
  );
};
